// @ts-check

import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

// core
import {
  AppContext,
  AuthContext,
  createCampaign,
  formatQueryCampaignMessage,
  OPEN_MODE,
  PAGE_STEP,
  resolveRouteWithId,
  routerUrl,
  scrollToElement,
  scrollTopPage,
  useScrollTop,
} from 'core';
import {
  Breadcrumb,
  CsvDataTable,
  ElementTooltip,
  PageTitle,
  processQuery,
  QueryFilterBuilder,
  QueryFilterPreview,
} from 'core/components';
import { previewTableConfig } from 'core/components/Tables/configs';
import {
  useStateCampaign,
  useStateFilters,
} from 'core/hooks/use-state-campaign';

// utils
import { campaignRefresh } from 'core/services/campaign-refresh-service';

// state
import { useAppDispatch, useAppSelector } from 'core/store';
import {
  createCampaignAsync,
  debounceStoreDraft,
  getDisplayContentAsync,
  setCampaignField,
  setCampaignPreviewLoading,
  setCampaignPreviewWebsocket,
  setErrorMessage,
  setPageSummary,
  setSelectedRoute,
  updateCampaignAsync,
} from 'core/store/slices';

// components
import { DisplayContent } from './components/DisplayContent';

const HeaderSection = styled.header`
  display: flex;
  align-items: center;
`;

const DictionaryHeader = styled.div`
  font-weight: bold;
  margin-left: 20px;
`;

const Icon = styled.i`
  margin-right: 10px;
`;

/**
 * Deep equality comparison that is not sensitive to property order.
 * Compare the objects by sorting their keys (eliminating order sensitivity),
 * comparing the number of keys, and recursively comparing each key's values.
 * @param {any} obj1
 * @param {any} obj2
 * @returns {boolean}
 */
const isDeepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== typeof obj2) return false;

  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
    return obj1.every((item, index) => isDeepEqual(item, obj2[index]));
  }

  if (typeof obj1 === 'object') {
    const keys1 = Object.keys(obj1).sort();
    const keys2 = Object.keys(obj2).sort();
    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => isDeepEqual(obj1[key], obj2[key]));
  }

  return obj1 === obj2;
};

/**
 * Determine if default filter should be applied for this campaign.
 * Mirrors the conditions used when setting the QueryFilterBuilder defaultValueQuery.
 * @param {string} openMode - The current open mode (CREATE/EDIT/VIEW).
 * @param {boolean} hasCampaignLogic - True when existing filter logic is persisted.
 * @returns {boolean}
 */
const shouldApplyDefaultFilter = (openMode, hasCampaignLogic) => {
  // Don't apply in VIEW mode.
  if (openMode === OPEN_MODE.VIEW) {
    return false;
  }

  // Don't apply if the campaign already has logic.
  if (hasCampaignLogic) {
    return false;
  }

  // Apply for campaigns in CREATE mode without logic (regardless of campaignId).
  // This handles the case where Apply Filters was clicked, creating a campaignId,
  // but the mode is still CREATE and no logic is saved yet.
  return openMode === OPEN_MODE.CREATE && !hasCampaignLogic;
};

/**
 * Create a default query with "Incoming Student Flag" = "N" for new campaigns only.
 * @returns {CB_RuleGroupType}
 */
const createDefaultQuery = () => {
  /** @type {CB_RuleGroupType} */
  const defaultQuery = {
    rules: [
      {
        field: 'incoming_student_flag',
        value: 'N',
        groupEnd: '',
        operator: '=',
        combinator: '',
        groupStart: '',
      },
    ],
  };
  return defaultQuery;
};

const CampaignDefineFilter = () => {
  const { user } = useContext(AuthContext);
  const { sendMessage, stopSendMessage } = useContext(AppContext);

  const dispatch = useAppDispatch();
  // Use individual selectors to avoid creating a new aggregated object reference each render.
  const campaignData = useAppSelector((state) => state.campaignData);
  const campaignPreview = useAppSelector((state) => state.campaignPreview);
  const queryBuilder = useAppSelector((state) => state.queryBuilder);
  const shared = useAppSelector((state) => state.shared);

  const { campaign, openMode } = campaignData;
  const campaignLogic = campaign?.logic;
  const campaignId = campaign?.campaignId;
  const hasCampaignLogic = Boolean(campaignLogic);
  const shouldUseDefaultFilter = shouldApplyDefaultFilter(
    openMode,
    hasCampaignLogic,
  );
  const readonly = openMode === OPEN_MODE.VIEW;

  const { loading: previewLoading, previewData } = campaignPreview;
  const { filters } = queryBuilder;

  const {
    feedback: { errorMessage },
  } = shared;

  useStateFilters();
  useStateCampaign();
  useScrollTop();

  /** @type {HistoryState} */
  const history = useHistory();

  /**
   * @type {[QueryFilterValue | undefined, React.Dispatch<React.SetStateAction<QueryFilterValue | undefined>>]}
   */
  const [queryOutput, setQueryOutput] = useState(undefined);
  const [hasValidRuleValues, setValidRuleValues] = useState(true);
  const [showQueryError, setShowQueryError] = useState(false);

  useEffect(() => {
    setQueryOutput((prev) => {
      if (campaignLogic) {
        return processQuery(campaignLogic);
      }

      const hasUserQuery = !!prev?.jsonQuery?.rules?.length;
      if (hasUserQuery) {
        return prev;
      }

      const shouldUseDefault = !campaignId || shouldUseDefaultFilter;

      if (shouldUseDefault) {
        const defaultQuery = createDefaultQuery();
        return processQuery(defaultQuery);
      }

      return prev;
    });
  }, [campaignLogic, campaignId, shouldUseDefaultFilter]);

  useEffect(() => {
    dispatch(setSelectedRoute(routerUrl.CAMPAIGN_DEFINE_FILTER));
    dispatch(setPageSummary('Step 2 of 2'));
    //TODO: Fix getDisplayContentAsync() call. It is expecting 1 parameter but currently passing none.
    // @ts-ignore
    dispatch(getDisplayContentAsync());
  }, [dispatch]);

  // Scroll user's browswer window to the top of page if there is an error message.
  useEffect(() => {
    if (!!errorMessage) {
      scrollTopPage();
    }
  }, [errorMessage]);

  const hasValidRules = () => {
    const len = queryOutput?.jsonQuery?.rules?.length;

    // Check if there are any rules in queryOutput.
    const hasRulesFromQueryOutput = len > 0 && hasValidRuleValues;

    // Check if there should be default rules (when queryOutput is undefined but default should apply).
    const shouldHaveDefaultRules =
      !queryOutput && shouldUseDefaultFilter && hasValidRuleValues;

    return hasRulesFromQueryOutput || shouldHaveDefaultRules;
  };

  const saveCampaign = async (e) => {
    e.preventDefault();

    if (!hasValidRules()) {
      setShowQueryError(true);
      scrollToElement('#query-builder');
      return;
    }

    // Determine the logic to save.
    let logicToSave;
    if (queryOutput?.jsonQuery) {
      // Use the current query output.
      logicToSave = queryOutput.jsonQuery;
    } else if (shouldUseDefaultFilter) {
      // Use default query if queryOutput is undefined but we should have defaults.
      logicToSave = createDefaultQuery();
    } else {
      // No logic available.
      logicToSave = null;
    }

    const newCampaign = {
      ...campaign,
      createdByName: user,
      logic: logicToSave,
    };

    const saveActionAsync = campaignId
      ? updateCampaignAsync
      : createCampaignAsync;

    try {
      const newCampaignId = await dispatch(
        saveActionAsync({
          campaign: newCampaign,
          reloadData: true,
          infoMessage: `Campaign has been ${
            campaign?.campaignId ? 'updated' : 'saved'
          }`,
        }),
      ).unwrap();

      // Only refresh campaign preview when:
      // 1. It's a new campaign, OR
      // 2. The filter logic changed but the current query has
      // not been applied (Apply Filters button clicked).
      // previewData.queryLogic should match current queryOutput.jsonQuery if
      // Apply Filters was clicked.
      const currentJsonQuery = queryOutput?.jsonQuery;
      const shouldRefreshCampaign =
        !campaignId ||
        (!isDeepEqual(campaignLogic, currentJsonQuery) &&
          !isDeepEqual(previewData.queryLogic, currentJsonQuery));

      if (shouldRefreshCampaign) {
        campaignRefresh({
          campaign: {
            ...newCampaign,
            campaignId: newCampaignId || campaignId,
          },
          sendMessage,
          dispatch,
        });
      }

      if (newCampaignId) {
        history.push(
          resolveRouteWithId(routerUrl.CAMPAIGN_DETAILS, newCampaignId),
        );
      }
    } catch (err) {
      // Error handled by Redux store error handling.
    }
  };

  const viewCampaign = () => {
    history.push(resolveRouteWithId(routerUrl.CAMPAIGN_DETAILS, campaignId));
  };

  const { displayContent, loading } = useAppSelector(
    (state) => state.displayContent,
  );

  /** @param {QueryFilterValue} output */
  const queryChange = (output) => {
    setQueryOutput(processQuery(output.jsonQuery));
    debounceStoreDraft(dispatch, { logic: output.jsonQuery });
  };

  const queryClean = () => {
    dispatch(
      setCampaignField({
        logic: null,
      }),
    );
  };

  const queryValidate = (isValidQuery) => {
    setValidRuleValues(isValidQuery);
  };

  const cancelApplyFilter = () => {
    dispatch(setCampaignPreviewLoading(false));
    stopSendMessage();
  };

  const applyFilter = async () => {
    dispatch(setCampaignPreviewLoading(true));
    // =============================================
    // Save campaign to get a new campaign ID.
    // The campaign object is read-only. Thus, if necessary, use
    // currentCampaignId to intermediately store newly created ID.
    /* TODO: consider to create the campaign when the user
    clicks `next` and then remove this code */
    // =============================================
    let currentCampaignId = campaignId;

    if (!campaignId) {
      const { campaign_id: newCampaignId } = await createCampaign({
        ...campaign,
        logic: queryOutput.jsonQuery,
      });

      // Intermediately store the newly created campaignId that is returned from campaignDataSlice.
      currentCampaignId = newCampaignId;

      if (newCampaignId) {
        dispatch(
          setCampaignField({
            campaignId: newCampaignId,
          }),
        );
      } else {
        dispatch(setCampaignPreviewLoading(false));
        dispatch(
          setErrorMessage(
            'At them moment is not possible to generate the filter preview.' +
              `Please, try again later.`,
          ),
        );
        console.error('ERROR: createCampaign() failed');
        return null;
      }
    }

    // =============================================
    // - Check if filter object reference has changed
    // =============================================
    if (queryOutput.jsonQuery === previewData.queryLogic) {
      setTimeout(() => dispatch(setCampaignPreviewLoading(false)), 100);
      return;
    }

    // =============================================
    // - Send message to websocket.
    // Set campaignId to the newly created (or current) campaignId.
    // =============================================
    const newCampaign = {
      ...campaign,
      logic: queryOutput.jsonQuery,
      campaignId: currentCampaignId,
    };
    const message = formatQueryCampaignMessage(newCampaign);

    sendMessage({
      handleError: true,
      message,
      onMessageCallback: (evt) => {
        dispatch(
          setCampaignPreviewWebsocket({
            wsData: evt.data,
            queryLogic: newCampaign?.logic,
          }),
        );
      },
    });
  };

  return (
    <section>
      <header>
        <HeaderSection>
          <div>
            <PageTitle title="Define filters" />
          </div>
          <DictionaryHeader>
            <span>
              <Icon className="fa-solid fa-up-right-from-square text-maroon" />
            </span>
            <span>
              <a
                href="https://docs.google.com/spreadsheets/d/1pdhpt_jn9wdIEVxcmOUJTb133QJBIYl0agPEqNxesHM/edit#gid=312242724"
                target="_blank"
                rel="noreferrer"
              >
                Filters Dictionary
              </a>
            </span>
          </DictionaryHeader>
        </HeaderSection>
        <Breadcrumb
          currentPage={PAGE_STEP.CAMPAIGN_FILTER}
          openMode={openMode}
          onRouteChange={() => {
            dispatch(
              setCampaignField({
                logic: queryOutput?.jsonQuery ?? null,
              }),
            );
          }}
        />
        <DisplayContent
          loading={loading}
          censusDate={displayContent.censusDate}
          currentTerm={displayContent.currentTerm}
          lastRefreshDate={displayContent.lastRefreshDate}
          priorTerm={displayContent.priorTerm}
          priorRegularTerm={displayContent.priorRegularTerm}
          nextRegularTerm={displayContent.nextRegularTerm}
          nextTerm={displayContent.nextTerm}
        />
        <h3>
          <span>{campaign?.campaignName}</span>
        </h3>
      </header>
      <section>
        <header>
          <h4>Population Filters</h4>
        </header>
        <p>
          Define the campaign's population of students with the filters below.
          The overall population of students the data are pulled from is defined
          as all living, plan stack active students.
        </p>
        <QueryFilterBuilder
          id="query-builder"
          checkErrors={showQueryError}
          readOnly={readonly}
          valueQuery={queryOutput?.valueQuery}
          defaultValueQuery={
            !queryOutput?.jsonQuery?.rules?.length && shouldUseDefaultFilter
              ? createDefaultQuery()
              : null
          }
          filters={filters}
          maxRows={20}
          applyFilterInProgress={previewLoading}
          onApplyFilter={applyFilter}
          onCancelApplyFilter={cancelApplyFilter}
          onQueryChange={queryChange}
          onQueryClean={queryClean}
          onQueryValidate={queryValidate}
        />
      </section>
      <section>
        <section className="container mt-4 p-0">
          <header>
            <h4>Campaign Preview</h4>
          </header>
          <article>
            <p>
              To see a preview of the data, please select the filters above.
              <br />
              The "Apply Filters" button will generate a preview of the data.
            </p>
          </article>
        </section>
      </section>

      <section>
        <QueryFilterPreview query={queryOutput} />
      </section>

      <section className="mb-2">
        <CsvDataTable
          loading={previewLoading}
          config={previewTableConfig}
          columns={previewData.columns}
          records={previewData.records}
          totalRecordCount={previewData.totalRecordCount}
          height="300px"
        />
      </section>

      <footer>
        <ElementTooltip
          tooltipId="save-campaign-tooltip"
          tooltip={
            'This allows for the Campaign List to be saved. ' +
            'It will save the list, logic, and metadata. ' +
            'Moving away from this page without clicking Save Campaign or Update Campaign will ' +
            'result in losing any changes that were made to your campaign.'
          }
          direction="right"
        >
          {campaign?.published || readonly ? (
            <button
              type="button"
              className="btn btn-md btn-maroon"
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Campaign Define Filter"
              data-ga-component=""
              data-ga-text="View details"
              onClick={viewCampaign}
            >
              View details
            </button>
          ) : (
            <button
              data-testid="bn-save"
              type="button"
              className="btn btn-md btn-maroon"
              aria-describedby="save-campaign-tooltip"
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Campaign Define Filter"
              data-ga-component=""
              data-ga-text={campaign?.campaignId ? 'Update Campaign' : 'Save Campaign'}
              onClick={saveCampaign}
            >
              {campaign?.campaignId ? 'Update Campaign' : 'Save Campaign'}
            </button>
          )}
        </ElementTooltip>
      </footer>
    </section>
  );
};

export { CampaignDefineFilter };
